/**
 * Apps Manager — create apps and assign them to groups_periods.
 * Assignments use relationships: parent_table='groups_periods', child_table='apps', relationship_type='app_assignment'.
 */
import React, { useState, useEffect, useMemo, useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import LoadingOverlay from '../components/overlays/LoadingOverlay';
import ErrorModal from '../components/modals/ErrorModal';
import SuccessModal from '../components/modals/SuccessModal';
import { useAuth } from '../contexts/AuthContext';
import { crudStyles } from '../utils/crudStyles';
import {
  APP_SLUGS,
  DEFAULT_STUDENT_INFO_ENABLED_ATTRIBUTES,
  getAppSlugDetails,
  listKnownAppDefinitions,
  PERSON_LINKS_REQUIRED_FIELD_OPTIONS,
  STUDENT_INFORMATION_FIELD_DEFS,
  STUDENT_INFO_MASKABLE_ATTRIBUTE_KEYS,
  STUDENT_INFORMATION_TABLE,
  normalizeStudentEnabledAttributes
} from '../constants/appSlugs';
import {
  DEFAULT_STUDENT_STATUS_OPTIONS,
  parseCommaSeparatedOptions
} from '../constants/studentInformationOptions';
import {
  formatRequiredCredentialLine,
  parseRequiredCredentialItems,
  parseRequiredCredentialLine
} from '../constants/credentialRequirements';
import { DEFAULT_PROPERTY_DEFINITIONS } from '../constants/ugpProperties';
import {
  getEnabledPersonLinkTypes,
  PERSON_LINK_TYPE_CARDS,
  PERSON_LINKS_RELATIONSHIP_TYPES
} from '../constants/personLinksOptions';
import {
  WORK_SAMPLE_GP_ROLE_FIELDS,
  TEACHER_OF_RECORD_LINK_TYPE,
  buildWorkSampleAssignmentMetadata,
  formatGroupsPeriodLabel,
  loadOrgTeamGpOptions,
  mergeGpOptionsWithSelections,
  parseWorkSampleRoleGpIds
} from '../constants/workSampleWorkflow';
import {
  DEFAULT_FLAG_REASON_CATALOG,
  parseFlagReasonsConfig,
  serializeFlagReasonsConfig,
  slugifyReasonCode,
  createEmptyFlagReasonEntry
} from '../constants/workSampleFlags';
import {
  DEFAULT_ASSOCIATION_TYPE_DEFS,
  normalizeAssociationType
} from '../constants/associations';
import {
  buildGroupsPeriodsHierarchyMeta,
  formatGroupsPeriodPickerLabel,
  getAssignGpDisabledReason,
  groupGroupsPeriodsForPicker,
  isOrgRootGroupsPeriod
} from '../utils/groupsPeriodsPicker';
import '../utils/crudStyles.css';

const IconPlus = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const IconLink = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const IconTrash = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
    <path d="M3 6h18M8 6V4h8v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6" />
  </svg>
);

const IconUnlink = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
    <path d="M18 6 6 18M8 8l-.9-.9a5 5 0 0 0 7.07 7.07l.71-.71M15.09 15.09a5 5 0 0 0-7.07-7.07l-.71.71" />
  </svg>
);

const IconSliders = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72 1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
  </svg>
);

const iconBtnBase = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  cursor: 'pointer',
  borderRadius: 8,
  transition: 'background 0.15s, color 0.15s'
};

function parseRelMetadata(rel) {
  const m = rel?.metadata;
  if (m == null) return {};
  if (typeof m === 'string') {
    try {
      const p = JSON.parse(m);
      return p && typeof p === 'object' && !Array.isArray(p) ? { ...p } : {};
    } catch {
      return {};
    }
  }
  if (typeof m === 'object' && !Array.isArray(m)) return { ...m };
  return {};
}

function humanizeAttrKey(key) {
  return String(key)
    .split('_')
    .map(w => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ''))
    .join(' ');
}

function slugHasInlineSettings(slug) {
  const s = String(slug || '').trim().toLowerCase();
  return (
    s === APP_SLUGS.STUDENT_INFORMATION ||
    s === APP_SLUGS.PERSON_LINKS ||
    s === APP_SLUGS.CREDENTIALS_AND_KEYS ||
    s === APP_SLUGS.CUSTOM_FORMS ||
    s === APP_SLUGS.ATTACHMENTS ||
    s === APP_SLUGS.PROFILE_PROPERTIES ||
    s === APP_SLUGS.PAYMENTS ||
    s === APP_SLUGS.ASSOCIATIONS ||
    s === APP_SLUGS.WORK_SAMPLES
  );
}

function associationTypesExcludingVendor() {
  return DEFAULT_ASSOCIATION_TYPE_DEFS.filter((d) => d.type !== 'vendor_partner');
}

function formatDefaultAssociationTypesText() {
  return associationTypesExcludingVendor()
    .map((d) => `${d.type}: ${d.label}`)
    .join('\n');
}

function parseAssociationTypesText(text) {
  const lines = String(text || '')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
  const defsByType = new Map(DEFAULT_ASSOCIATION_TYPE_DEFS.map((d) => [d.type, d]));
  const parsed = lines
    .map((line) => {
      const colon = line.indexOf(':');
      const typeRaw = colon >= 0 ? line.slice(0, colon) : line;
      const labelRaw = colon >= 0 ? line.slice(colon + 1) : '';
      const type = normalizeAssociationType(typeRaw);
      if (!type) return null;
      const def = defsByType.get(type) || {};
      const label = labelRaw.trim() || def.label || type;
      return { type, label };
    })
    .filter(Boolean);
  return parsed.length
    ? parsed
    : associationTypesExcludingVendor().map((d) => ({ type: d.type, label: d.label }));
}

const AssignmentSettingsForm = forwardRef(function AssignmentSettingsForm(
  { rel, appSlug, st, allowJsonFallback = true, groupsPeriods = [], assignmentParentGpId = '' },
  ref
) {
  const slug = String(appSlug || '').trim().toLowerCase();

  const [stLabel, setStLabel] = useState('');
  /** Ordered keys written to metadata.enabled_attributes (= User Profile field order). */
  const [stOrder, setStOrder] = useState(() => []);
  const [stMasked, setStMasked] = useState(() => new Set());
  const [stGradeOpts, setStGradeOpts] = useState('');
  const [stStatusOpts, setStStatusOpts] = useState('');
  const [stVendorRoots, setStVendorRoots] = useState('');

  const [plLabel, setPlLabel] = useState('');
  const [plEnabledTypes, setPlEnabledTypes] = useState(() => new Set(PERSON_LINKS_RELATIONSHIP_TYPES));
  const [plReq, setPlReq] = useState(() => new Set());
  const [plShowCounselorMeetingLink, setPlShowCounselorMeetingLink] = useState(false);
  const [plCounselorVisibleProps, setPlCounselorVisibleProps] = useState(() => new Set());

  const [ppLabel, setPpLabel] = useState('');
  const [ppEnabledProps, setPpEnabledProps] = useState(() => new Set(['meeting_link']));

  const [cfLabel, setCfLabel] = useState('');

  const [attLabel, setAttLabel] = useState('');
  const [attAllowUpload, setAttAllowUpload] = useState(true);
  const [attCategoriesText, setAttCategoriesText] = useState('');
  const [attRequiredText, setAttRequiredText] = useState('');

  const [pcLabel, setPcLabel] = useState('');
  const [pcControl, setPcControl] = useState('both');
  const [pcLockedText, setPcLockedText] = useState('');
  const [pcRequiredText, setPcRequiredText] = useState('');

  const [payLabel, setPayLabel] = useState('');

  const [assocLabel, setAssocLabel] = useState('');
  const [assocEnabledTypesText, setAssocEnabledTypesText] = useState(() => formatDefaultAssociationTypesText());

  const [wsStudentGpId, setWsStudentGpId] = useState('');
  const [wsVdmGpId, setWsVdmGpId] = useState('');
  const [wsComplianceGpId, setWsComplianceGpId] = useState('');
  const [wsOrgTeamGps, setWsOrgTeamGps] = useState([]);
  const [wsOrgLabel, setWsOrgLabel] = useState('');
  const [wsOrgGpLoading, setWsOrgGpLoading] = useState(false);
  const [wsOrgGpError, setWsOrgGpError] = useState('');
  const [wsFlagReasons, setWsFlagReasons] = useState(() => [...DEFAULT_FLAG_REASON_CATALOG]);

  const [expandedByDefault, setExpandedByDefault] = useState(false);

  const [jsonStr, setJsonStr] = useState('{}');

  useEffect(() => {
    const base = parseRelMetadata(rel);
    setExpandedByDefault(base.expanded_by_default === true);
    if (slug === APP_SLUGS.STUDENT_INFORMATION) {
      const order = Array.isArray(base.enabled_attributes)
        ? normalizeStudentEnabledAttributes(base.enabled_attributes)
        : [...DEFAULT_STUDENT_INFO_ENABLED_ATTRIBUTES];
      setStLabel(typeof base.container_label_override === 'string' ? base.container_label_override : '');
      setStOrder(order);
      setStMasked(new Set(Array.isArray(base.masked_attributes) ? base.masked_attributes : []));
      setStGradeOpts(Array.isArray(base.grade_level_options) ? base.grade_level_options.join(', ') : '');
      setStStatusOpts(Array.isArray(base.status_options) ? base.status_options.join(', ') : '');
      setStVendorRoots(
        Array.isArray(base.vendor_allowed_gp_roots) ? base.vendor_allowed_gp_roots.join('\n') : ''
      );
    } else if (slug === APP_SLUGS.PERSON_LINKS) {
      setPlLabel(typeof base.container_label_override === 'string' ? base.container_label_override : '');
      setPlEnabledTypes(new Set(getEnabledPersonLinkTypes(base)));
      const rf = (Array.isArray(base.required_fields) ? base.required_fields : []).map(x => String(x).toLowerCase());
      setPlReq(new Set(rf.filter(x => x === 'email' || x === 'phone')));
      setPlShowCounselorMeetingLink(base.show_counselor_meeting_link === true);
      const vis = Array.isArray(base.counselor_visible_properties)
        ? base.counselor_visible_properties.map((x) => String(x).toLowerCase())
        : base.show_counselor_meeting_link === true
          ? ['meeting_link']
          : [];
      setPlCounselorVisibleProps(new Set(vis.filter(Boolean)));
    } else if (slug === APP_SLUGS.PROFILE_PROPERTIES) {
      setPpLabel(typeof base.container_label_override === 'string' ? base.container_label_override : '');
      const en = Array.isArray(base.enabled_properties)
        ? base.enabled_properties.map((x) => String(x).toLowerCase())
        : ['meeting_link'];
      setPpEnabledProps(new Set(en.filter(Boolean)));
    } else if (slug === APP_SLUGS.CREDENTIALS_AND_KEYS) {
      setPcLabel(typeof base.container_label_override === 'string' ? base.container_label_override : '');
      const c = String(base.credential_control || 'both').toLowerCase();
      setPcControl(['locked_only', 'freeform_only', 'both'].includes(c) ? c : 'both');
      setPcLockedText(Array.isArray(base.locked_platforms) ? base.locked_platforms.join(', ') : '');
      const reqItems = parseRequiredCredentialItems(base);
      setPcRequiredText(
        reqItems.length ? reqItems.map(formatRequiredCredentialLine).join('\n') : ''
      );
    } else if (slug === APP_SLUGS.CUSTOM_FORMS) {
      setCfLabel(typeof base.container_label_override === 'string' ? base.container_label_override : '');
    } else if (slug === APP_SLUGS.PAYMENTS) {
      setPayLabel(typeof base.container_label_override === 'string' ? base.container_label_override : '');
    } else if (slug === APP_SLUGS.ASSOCIATIONS) {
      setAssocLabel(typeof base.container_label_override === 'string' ? base.container_label_override : '');
      const types = Array.isArray(base.enabled_association_types) ? base.enabled_association_types : [];
      if (types.length) {
        setAssocEnabledTypesText(
          types
            .map((item) => {
              if (typeof item === 'string') return item;
              if (item && typeof item === 'object' && item.type) {
                return `${item.type}: ${item.label || item.type}`;
              }
              return '';
            })
            .filter(Boolean)
            .join('\n')
        );
      } else {
        setAssocEnabledTypesText(formatDefaultAssociationTypesText());
      }
    } else if (slug === APP_SLUGS.WORK_SAMPLES) {
      const parsed = parseWorkSampleRoleGpIds(base);
      setWsStudentGpId(parsed.studentGpId);
      setWsVdmGpId(parsed.vdmGpId);
      setWsComplianceGpId(parsed.complianceGpId);
      setWsFlagReasons(parseFlagReasonsConfig(base) || [...DEFAULT_FLAG_REASON_CATALOG]);
    } else if (slug === APP_SLUGS.ATTACHMENTS) {
      setAttLabel(typeof base.container_label_override === 'string' ? base.container_label_override : '');
      setAttAllowUpload(base.allow_upload !== false);
      setAttCategoriesText(
        Array.isArray(base.allowed_categories) ? base.allowed_categories.join('\n') : ''
      );
      const reqRaw = base.required_attachments ?? base.required_attachment_titles;
      if (Array.isArray(reqRaw)) {
        setAttRequiredText(
          reqRaw
            .map((item) =>
              typeof item === 'string' ? item : String(item?.title ?? item?.label ?? '')
            )
            .filter(Boolean)
            .join('\n')
        );
      } else {
        setAttRequiredText('');
      }
    } else {
      try {
        setJsonStr(JSON.stringify(Object.keys(base).length ? base : {}, null, 2));
      } catch {
        setJsonStr('{}');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-init when switching assignment or app type; rel.metadata is read on open
  }, [rel?.id, slug]);

  const linkedAssignmentGpId = String(assignmentParentGpId || rel?.parent_id || '').trim();

  useEffect(() => {
    if (slug !== APP_SLUGS.WORK_SAMPLES) return undefined;
    if (!linkedAssignmentGpId) {
      setWsOrgTeamGps([]);
      setWsOrgLabel('');
      setWsOrgGpError('');
      setWsOrgGpLoading(false);
      return undefined;
    }

    let cancelled = false;
    setWsOrgGpLoading(true);
    setWsOrgGpError('');

    loadOrgTeamGpOptions(groupsPeriods, linkedAssignmentGpId)
      .then(({ orgGpId, orgLabel, teamGps }) => {
        if (cancelled) return;
        if (!orgGpId) {
          setWsOrgTeamGps([]);
          setWsOrgLabel('');
          setWsOrgGpError('Could not resolve the organization for this assignment.');
          return;
        }
        setWsOrgTeamGps(teamGps);
        setWsOrgLabel(orgLabel);
        setWsOrgGpError('');
      })
      .catch((e) => {
        if (cancelled) return;
        setWsOrgTeamGps([]);
        setWsOrgLabel('');
        setWsOrgGpError(e.message || 'Failed to load organization teams');
      })
      .finally(() => {
        if (!cancelled) setWsOrgGpLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug, linkedAssignmentGpId, groupsPeriods]);

  const wsGpOptions = useMemo(() => {
    return mergeGpOptionsWithSelections(wsOrgTeamGps, groupsPeriods, [
      wsStudentGpId,
      wsVdmGpId,
      wsComplianceGpId
    ]);
  }, [wsOrgTeamGps, groupsPeriods, wsStudentGpId, wsVdmGpId, wsComplianceGpId]);

  const orderedFieldDefs = useMemo(() => {
    const inOrder = new Set(stOrder);
    const primary = stOrder.map(k => STUDENT_INFORMATION_FIELD_DEFS.find(d => d.key === k)).filter(Boolean);
    const tail = STUDENT_INFORMATION_FIELD_DEFS.filter(d => !inOrder.has(d.key));
    return [...primary, ...tail];
  }, [stOrder]);

  const toggleStudentField = (k) => {
    setStOrder(prev => {
      if (prev.includes(k)) {
        setStMasked(m => {
          const nm = new Set(m);
          nm.delete(k);
          return nm;
        });
        return prev.filter(x => x !== k);
      }
      return [...prev, k];
    });
  };

  const moveStudentField = (index, delta) => {
    setStOrder(prev => {
      const j = index + delta;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
  };

  const toggleStudentMasked = (k) => {
    setStMasked(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const togglePlReq = (v) => {
    setPlReq(prev => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });
  };

  const togglePlEnabledType = (type) => {
    setPlEnabledTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        if (next.size <= 1) return prev;
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const applyExpandedByDefault = (meta) => {
    if (expandedByDefault) meta.expanded_by_default = true;
    else delete meta.expanded_by_default;
    return meta;
  };

  const buildMetadata = () => {
    const base = parseRelMetadata(rel);
    if (slug === APP_SLUGS.STUDENT_INFORMATION) {
      const meta = { ...base };
      meta.enabled_attributes = [...stOrder];
      meta.masked_attributes = STUDENT_INFO_MASKABLE_ATTRIBUTE_KEYS.filter(k => stMasked.has(k) && stOrder.includes(k));
      meta.exitdate_sync_popup = true;
      const gradeOpts = parseCommaSeparatedOptions(stGradeOpts);
      if (gradeOpts.length) meta.grade_level_options = gradeOpts;
      else delete meta.grade_level_options;
      const statusOpts = parseCommaSeparatedOptions(stStatusOpts);
      if (statusOpts.length) meta.status_options = statusOpts;
      else delete meta.status_options;
      const vendorRoots = stVendorRoots
        .split(/[\n,]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (vendorRoots.length) meta.vendor_allowed_gp_roots = vendorRoots;
      else delete meta.vendor_allowed_gp_roots;
      if (stLabel.trim()) meta.container_label_override = stLabel.trim();
      else delete meta.container_label_override;
      return applyExpandedByDefault(meta);
    }
    if (slug === APP_SLUGS.PERSON_LINKS) {
      const meta = { ...base };
      if (plLabel.trim()) meta.container_label_override = plLabel.trim();
      else delete meta.container_label_override;
      const enabledArr = PERSON_LINKS_RELATIONSHIP_TYPES.filter((t) => plEnabledTypes.has(t));
      if (!enabledArr.length) {
        throw new Error('Enable at least one person link relationship type.');
      }
      if (enabledArr.length < PERSON_LINKS_RELATIONSHIP_TYPES.length) {
        meta.enabled_relationship_types = enabledArr;
      } else {
        delete meta.enabled_relationship_types;
      }
      const req = PERSON_LINKS_REQUIRED_FIELD_OPTIONS.map(o => o.value).filter(v => plReq.has(v));
      if (req.length) meta.required_fields = req;
      else delete meta.required_fields;
      const visKeys = Object.keys(DEFAULT_PROPERTY_DEFINITIONS).filter((k) =>
        plCounselorVisibleProps.has(k)
      );
      if (visKeys.length) meta.counselor_visible_properties = visKeys;
      else delete meta.counselor_visible_properties;
      if (visKeys.includes('meeting_link')) meta.show_counselor_meeting_link = true;
      else delete meta.show_counselor_meeting_link;
      return applyExpandedByDefault(meta);
    }
    if (slug === APP_SLUGS.PROFILE_PROPERTIES) {
      const meta = { ...base };
      if (ppLabel.trim()) meta.container_label_override = ppLabel.trim();
      else delete meta.container_label_override;
      const enabled = Object.keys(DEFAULT_PROPERTY_DEFINITIONS).filter((k) => ppEnabledProps.has(k));
      if (enabled.length) meta.enabled_properties = enabled;
      else delete meta.enabled_properties;
      meta.property_definitions = { ...DEFAULT_PROPERTY_DEFINITIONS };
      return applyExpandedByDefault(meta);
    }
    if (slug === APP_SLUGS.CREDENTIALS_AND_KEYS) {
      const meta = { ...base };
      if (pcLabel.trim()) meta.container_label_override = pcLabel.trim();
      else delete meta.container_label_override;
      meta.credential_control = pcControl;
      const arr = pcLockedText.split(',').map(s => s.trim()).filter(Boolean);
      if (arr.length) meta.locked_platforms = arr;
      else delete meta.locked_platforms;
      if (pcControl === 'locked_only' && !arr.length) {
        throw new Error('When credential control is "Locked list only", add at least one platform in the locked list.');
      }
      const reqItems = pcRequiredText
        .split('\n')
        .map((line) => parseRequiredCredentialLine(line))
        .filter(Boolean);
      if (reqItems.length) {
        meta.required_credentials = reqItems.map(({ title, entryType }) => {
          const item = { title };
          if (entryType !== 'any') item.entry_type = entryType;
          return item;
        });
      } else {
        delete meta.required_credentials;
        delete meta.required_credential_titles;
      }
      return applyExpandedByDefault(meta);
    }
    if (slug === APP_SLUGS.CUSTOM_FORMS) {
      const meta = { ...base };
      if (cfLabel.trim()) meta.container_label_override = cfLabel.trim();
      else delete meta.container_label_override;
      return applyExpandedByDefault(meta);
    }
    if (slug === APP_SLUGS.PAYMENTS) {
      const meta = { ...base };
      if (payLabel.trim()) meta.container_label_override = payLabel.trim();
      else delete meta.container_label_override;
      return applyExpandedByDefault(meta);
    }
    if (slug === APP_SLUGS.ASSOCIATIONS) {
      const meta = { ...base };
      if (assocLabel.trim()) meta.container_label_override = assocLabel.trim();
      else delete meta.container_label_override;
      meta.enabled_association_types = parseAssociationTypesText(assocEnabledTypesText);
      return applyExpandedByDefault(meta);
    }
    if (slug === APP_SLUGS.WORK_SAMPLES) {
      return buildWorkSampleAssignmentMetadata({
        studentGpId: wsStudentGpId,
        vdmGpId: wsVdmGpId,
        complianceGpId: wsComplianceGpId,
        flagReasons: serializeFlagReasonsConfig(
          wsFlagReasons.filter((entry) => String(entry.label || '').trim() && String(entry.code || '').trim())
        )
      });
    }
    if (slug === APP_SLUGS.ATTACHMENTS) {
      const meta = { ...base };
      if (attLabel.trim()) meta.container_label_override = attLabel.trim();
      else delete meta.container_label_override;
      if (attAllowUpload === false) meta.allow_upload = false;
      else delete meta.allow_upload;
      const cats = attCategoriesText
        .split(/[\n,]+/)
        .map((s) => s.trim().toLowerCase().replace(/\s+/g, '_'))
        .filter(Boolean);
      if (cats.length) meta.allowed_categories = cats;
      else delete meta.allowed_categories;
      const reqTitles = attRequiredText
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      if (reqTitles.length) meta.required_attachments = reqTitles;
      else {
        delete meta.required_attachments;
        delete meta.required_attachment_titles;
      }
      return applyExpandedByDefault(meta);
    }
    let parsed;
    try {
      parsed = JSON.parse(jsonStr);
    } catch {
      throw new Error('Invalid JSON in metadata');
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error('Metadata must be a JSON object');
    }
    return parsed;
  };

  useImperativeHandle(ref, () => ({
    buildMetadata
  }));

  const hint = slug === APP_SLUGS.STUDENT_INFORMATION
    ? 'Each key maps to a column on public.student_info (grade, status, vendor partner FA track, schedule track, etc.). In Fields, order ↑↓ is profile order; vendor roots and grade options apply on their field rows.'
    : slug === APP_SLUGS.PERSON_LINKS
      ? 'Section title and required fields when creating linked people from profile.'
      : slug === APP_SLUGS.CREDENTIALS_AND_KEYS
        ? 'Platform picker settings, optional locked list, and required credential titles (matched on profile by credentials_keys.title).'
        : slug === APP_SLUGS.CUSTOM_FORMS
          ? 'Overrides the left-column section title for custom forms.'
          : slug === APP_SLUGS.ATTACHMENTS
            ? 'Section title, categories, uploads, and required attachment titles (matched on profile by document title).'
            : slug === APP_SLUGS.PROFILE_PROPERTIES
              ? 'Which properties may be added on profile; values stored on relationships (audience-scoped).'
              : slug === APP_SLUGS.PAYMENTS
                ? 'Section title only. Set the monthly payment schedule (amounts, due dates) on the Payments page (/payments).'
                : slug === APP_SLUGS.ASSOCIATIONS
                  ? 'Optional UGP → group-period links without membership (e.g. program site). Vendor partner / FA track lives on Student Information (student_info), not here.'
                  : slug === APP_SLUGS.WORK_SAMPLES
                  ? 'Org-level workflow switch and role registry. Assign on the organization\'s group-period; use start/end dates to toggle the workflow.'
                  : 'Full replacement metadata object for this assignment (advanced).';

  const showJsonFallback = allowJsonFallback &&
    slug !== APP_SLUGS.STUDENT_INFORMATION &&
    slug !== APP_SLUGS.PERSON_LINKS &&
    slug !== APP_SLUGS.CREDENTIALS_AND_KEYS &&
    slug !== APP_SLUGS.CUSTOM_FORMS &&
    slug !== APP_SLUGS.ATTACHMENTS &&
    slug !== APP_SLUGS.PROFILE_PROPERTIES &&
    slug !== APP_SLUGS.PAYMENTS &&
    slug !== APP_SLUGS.ASSOCIATIONS &&
    slug !== APP_SLUGS.WORK_SAMPLES;

  const wsGpValues = {
    student: wsStudentGpId,
    vdm: wsVdmGpId,
    compliance: wsComplianceGpId
  };

  const setWsGpValue = (roleKey, value) => {
    if (roleKey === 'student') setWsStudentGpId(value);
    else if (roleKey === 'vdm') setWsVdmGpId(value);
    else if (roleKey === 'compliance') setWsComplianceGpId(value);
  };

  const updateWsFlagReason = (index, patch) => {
    setWsFlagReasons((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, ...patch } : entry))
    );
  };

  const moveWsFlagReason = (index, delta) => {
    setWsFlagReasons((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      const tmp = next[index];
      next[index] = next[target];
      next[target] = tmp;
      return next.map((entry, i) => ({ ...entry, sort: i + 1 }));
    });
  };

  const addWsFlagReason = () => {
    setWsFlagReasons((prev) => [
      ...prev,
      createEmptyFlagReasonEntry(prev.length + 1)
    ]);
  };

  const removeWsFlagReason = (index) => {
    setWsFlagReasons((prev) => prev.filter((_, i) => i !== index));
  };

  const supportsExpandedByDefault =
    slug === APP_SLUGS.STUDENT_INFORMATION ||
    slug === APP_SLUGS.PERSON_LINKS ||
    slug === APP_SLUGS.CREDENTIALS_AND_KEYS ||
    slug === APP_SLUGS.CUSTOM_FORMS ||
    slug === APP_SLUGS.ATTACHMENTS ||
    slug === APP_SLUGS.PROFILE_PROPERTIES ||
    slug === APP_SLUGS.PAYMENTS ||
    slug === APP_SLUGS.ASSOCIATIONS;

  return (
    <>
      <p style={st.metaHint}>{hint}</p>

        {supportsExpandedByDefault && (
          <div style={{ ...st.field, display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              id={`expanded-by-default-${rel?.id || 'new'}`}
              type="checkbox"
              checked={expandedByDefault}
              onChange={(e) => setExpandedByDefault(e.target.checked)}
            />
            <label
              htmlFor={`expanded-by-default-${rel?.id || 'new'}`}
              style={{ ...st.label, marginBottom: 0, cursor: 'pointer' }}
            >
              Expanded by default on user profile
            </label>
          </div>
        )}

        {slug === APP_SLUGS.STUDENT_INFORMATION && (
          <>
            <p style={st.stDbNote}>
              <strong>{STUDENT_INFORMATION_TABLE}</strong> — one row per student <code style={st.inlineCode}>user_groups_periods_id</code>.
              Persisted via <code style={st.inlineCode}>PUT /api/student-info</code>. Includes{' '}
              <code style={st.inlineCode}>vendor_groups_periods_id</code> (FA / vendor partner for Work Samples),{' '}
              <code style={st.inlineCode}>track_period_id</code> (scheduler track), and{' '}
              <code style={st.inlineCode}>primary_pathway</code> (taxonomy concentration). Catalog:{' '}
              <code style={st.inlineCode}>client/src/constants/studentInformationFields.js</code>.
            </p>
            <div style={st.field}>
              <label style={st.label}>Section title override</label>
              <input
                type="text"
                value={stLabel}
                onChange={e => setStLabel(e.target.value)}
                placeholder="Default: Student Information"
                style={st.input}
              />
            </div>
            <div style={st.field}>
              <span style={st.label}>Fields</span>
              <p style={st.stCatalogHint}>
                Enable fields for this assignment, set <strong>profile order</strong> with ↑↓ when enabled, <strong>mask</strong> sensitive columns where offered.
                Enable <strong>Vendor partner</strong> and <strong>Schedule track</strong> for Work Samples / scheduler routing (changes append to{' '}
                <code style={st.inlineCode}>update_history</code>). Order is saved as{' '}
                <code style={st.inlineCode}>enabled_attributes</code>.
              </p>
              <div style={st.stFieldCatalog}>
                {orderedFieldDefs.map(def => {
                  const on = stOrder.includes(def.key);
                  const idxInOrder = stOrder.indexOf(def.key);
                  return (
                    <div key={def.key} style={{ ...st.stFieldRow, opacity: on ? 1 : 0.85 }}>
                      <div style={st.stFieldRowTop}>
                        <label style={st.stFieldCheck}>
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => toggleStudentField(def.key)}
                          />
                          <span style={st.stFieldTitle}>{def.label}</span>
                        </label>
                        {on ? (
                          <div style={st.stFieldOrderControls}>
                            <span style={st.stPosBadge} title="Position on User Profile">{idxInOrder + 1}</span>
                            <button
                              type="button"
                              style={{ ...st.stOrderBtn, opacity: idxInOrder === 0 ? 0.35 : 1 }}
                              onClick={() => moveStudentField(idxInOrder, -1)}
                              disabled={idxInOrder === 0}
                              title="Move up"
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              style={{ ...st.stOrderBtn, opacity: idxInOrder === stOrder.length - 1 ? 0.35 : 1 }}
                              onClick={() => moveStudentField(idxInOrder, 1)}
                              disabled={idxInOrder === stOrder.length - 1}
                              title="Move down"
                            >
                              ↓
                            </button>
                          </div>
                        ) : null}
                      </div>
                      <div style={st.stFieldMeta}>
                        <span style={st.stBadge}>{def.uiInputType}</span>
                        <code style={st.stMono}>{def.dbColumn}</code>
                        <span style={st.stDbType}>{def.dbType}</span>
                      </div>
                      {on && def.maskable ? (
                        <label style={st.stMaskInline}>
                          <input
                            type="checkbox"
                            checked={stMasked.has(def.key)}
                            onChange={() => toggleStudentMasked(def.key)}
                          />
                          <span>Mask in profile (click-to-reveal)</span>
                        </label>
                      ) : null}
                      {on && def.key === 'grade_level' ? (
                        <div style={st.stGradeOptsInline}>
                          <label style={st.stGradeOptsLabel}>Grade level options (comma-separated)</label>
                          <input
                            type="text"
                            value={stGradeOpts}
                            onChange={e => setStGradeOpts(e.target.value)}
                            placeholder="e.g. 9, 10, 11, 12 — empty = free text"
                            style={st.input}
                          />
                          <p style={st.stGradeOptsHint}>Saved as <code style={st.inlineCode}>grade_level_options</code> on this assignment.</p>
                        </div>
                      ) : null}
                      {on && def.key === 'status' ? (
                        <div style={st.stGradeOptsInline}>
                          <label style={st.stGradeOptsLabel}>Status options (comma-separated)</label>
                          <input
                            type="text"
                            value={stStatusOpts}
                            onChange={(e) => setStStatusOpts(e.target.value)}
                            placeholder={DEFAULT_STUDENT_STATUS_OPTIONS.join(', ')}
                            style={st.input}
                          />
                          <p style={st.stGradeOptsHint}>
                            Saved as <code style={st.inlineCode}>status_options</code> on this assignment (relationship
                            metadata). Empty = default Active, Withdrawn, Exited, Completed. Ended statuses still use{' '}
                            <code style={st.inlineCode}>exitdate_sync_popup</code> when exit date is enabled.
                          </p>
                        </div>
                      ) : null}
                      {on && def.key === 'vendor_groups_periods_id' ? (
                        <div style={st.stGradeOptsInline}>
                          <label style={st.stGradeOptsLabel}>
                            Vendor allowed group-period roots (one per line or comma-separated)
                          </label>
                          <textarea
                            value={stVendorRoots}
                            onChange={(e) => setStVendorRoots(e.target.value)}
                            placeholder="Optional — empty = all team groups under org. *descendants*&lt;gp_id&gt; = nested teams only (not the root). Add the plain gp_id too to include the root."
                            rows={3}
                            style={{ ...st.input, resize: 'vertical' }}
                          />
                          <p style={st.stGradeOptsHint}>
                            Saved as <code style={st.inlineCode}>vendor_allowed_gp_roots</code> — limits the vendor
                            partner picker on User Profile.
                          </p>
                        </div>
                      ) : null}
                      {def.uiDetail ? <p style={st.stFieldDetail}>{def.uiDetail}</p> : null}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {slug === APP_SLUGS.PERSON_LINKS && (
          <>
            <div style={st.field}>
              <label style={st.label}>Section title override</label>
              <input
                type="text"
                value={plLabel}
                onChange={e => setPlLabel(e.target.value)}
                placeholder="Default: Person Links"
                style={st.input}
              />
            </div>
            <div style={st.field}>
              <span style={st.label}>Enabled relationship types</span>
              <p style={st.stGradeOptsHint}>
                Controls which link types appear when adding person links on profiles with this assignment.
                Saved as <code style={st.inlineCode}>enabled_relationship_types</code> when not all types are
                checked.
              </p>
              <div style={st.checkboxGrid}>
                {PERSON_LINK_TYPE_CARDS.map(({ value, label, linkShape }) => (
                  <label key={value} style={st.checkboxRow}>
                    <input
                      type="checkbox"
                      checked={plEnabledTypes.has(value)}
                      onChange={() => togglePlEnabledType(value)}
                    />
                    <span>
                      {label} <span style={{ color: '#64748b' }}>({linkShape})</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div style={st.field}>
              <span style={st.label}>Required when creating a linked person</span>
              <div style={st.checkboxGrid}>
                {PERSON_LINKS_REQUIRED_FIELD_OPTIONS.map(({ value, label }) => (
                  <label key={value} style={st.checkboxRow}>
                    <input
                      type="checkbox"
                      checked={plReq.has(value)}
                      onChange={() => togglePlReq(value)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div style={st.field}>
              <span style={st.label}>Show on counselor person-link rows</span>
              <p style={st.stGradeOptsHint}>
                Resolved from Profile Properties on the linked person&apos;s UGP (audience must match the
                student&apos;s context). Enable Profile Properties on counselor group-periods to edit values.
              </p>
              <div style={st.checkboxGrid}>
                {Object.entries(DEFAULT_PROPERTY_DEFINITIONS).map(([key, def]) => (
                  <label key={key} style={st.checkboxRow}>
                    <input
                      type="checkbox"
                      checked={plCounselorVisibleProps.has(key)}
                      onChange={() => {
                        setPlCounselorVisibleProps((prev) => {
                          const next = new Set(prev);
                          if (next.has(key)) next.delete(key);
                          else next.add(key);
                          return next;
                        });
                      }}
                    />
                    <span>{def.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </>
        )}

        {slug === APP_SLUGS.PROFILE_PROPERTIES && (
          <>
            <p style={st.stDbNote}>
              <strong>relationships</strong> — parent = owner <code style={st.inlineCode}>user_groups_periods</code>,
              child = audience, <code style={st.inlineCode}>relationship_type</code> = property key,{' '}
              <code style={st.inlineCode}>value</code> = URL/text. API:{' '}
              <code style={st.inlineCode}>/api/ugp-properties</code>.
            </p>
            <div style={st.field}>
              <label style={st.label}>Section title override</label>
              <input
                type="text"
                value={ppLabel}
                onChange={(e) => setPpLabel(e.target.value)}
                placeholder="Default: Profile Properties"
                style={st.input}
              />
            </div>
            <div style={st.field}>
              <span style={st.label}>Properties users may add</span>
              <div style={st.checkboxGrid}>
                {Object.entries(DEFAULT_PROPERTY_DEFINITIONS).map(([key, def]) => (
                  <label key={key} style={st.checkboxRow}>
                    <input
                      type="checkbox"
                      checked={ppEnabledProps.has(key)}
                      onChange={() => {
                        setPpEnabledProps((prev) => {
                          const next = new Set(prev);
                          if (next.has(key)) next.delete(key);
                          else next.add(key);
                          return next;
                        });
                      }}
                    />
                    <span>{def.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </>
        )}

        {slug === APP_SLUGS.CREDENTIALS_AND_KEYS && (
          <>
            <p style={st.stDbNote}>
              <strong>credentials_keys</strong> — many rows per <code style={st.inlineCode}>user_groups_periods_id</code>. API:{' '}
              <code style={st.inlineCode}>/api/credentials-keys</code>.
            </p>
            <div style={st.field}>
              <label style={st.label}>Section title override</label>
              <input
                type="text"
                value={pcLabel}
                onChange={e => setPcLabel(e.target.value)}
                placeholder="Default: Credentials & Keys"
                style={st.input}
              />
            </div>
            <div style={st.field}>
              <label style={st.label}>Credential control</label>
              <select value={pcControl} onChange={e => setPcControl(e.target.value)} style={st.input}>
                <option value="locked_only">Locked list only — users pick only from the list below</option>
                <option value="freeform_only">Freeform only — users type any platform name</option>
                <option value="both">Both — pick from list or choose &quot;Other&quot; and type a name</option>
              </select>
            </div>
            <div style={st.field}>
              <label style={st.label}>Locked platforms (comma-separated)</label>
              <input
                type="text"
                value={pcLockedText}
                onChange={e => setPcLockedText(e.target.value)}
                placeholder="e.g. Google Classroom, School LMS, Library"
                style={st.input}
              />
              <p style={st.stGradeOptsHint}>
                Saved as <code style={st.inlineCode}>locked_platforms</code>. Required when control is &quot;Locked list only&quot;; optional for &quot;Both&quot;.
              </p>
            </div>
            <div style={st.field}>
              <label style={st.label}>Required credentials / keys by title (one per line)</label>
              <textarea
                value={pcRequiredText}
                onChange={(e) => setPcRequiredText(e.target.value)}
                rows={5}
                placeholder={'Google Classroom Login\ncredential: State Testing Login\nkey: SIS API Token'}
                style={st.textarea}
                spellCheck={false}
              />
              <p style={st.stGradeOptsHint}>
                Plain title = satisfied by a <strong>credential or key</strong> with that title. Use{' '}
                <code style={st.inlineCode}>credential:</code> or <code style={st.inlineCode}>key:</code> to require a
                specific type. Saved as <code style={st.inlineCode}>required_credentials</code> (
                <code style={st.inlineCode}>entry_type</code> only when type is fixed).
              </p>
            </div>
          </>
        )}

        {slug === APP_SLUGS.CUSTOM_FORMS && (
          <div style={st.field}>
            <label style={st.label}>Section title override</label>
            <input
              type="text"
              value={cfLabel}
              onChange={e => setCfLabel(e.target.value)}
              placeholder="Default: Custom Forms"
              style={st.input}
            />
          </div>
        )}

        {slug === APP_SLUGS.ASSOCIATIONS && (
          <>
            <p style={st.stDbNote}>
              <strong>associations</strong> — optional <code style={st.inlineCode}>relationships</code> rows with{' '}
              <code style={st.inlineCode}>source_slug=associations</code> (program site, legacy types).{' '}
              <strong>Vendor partner / FA track</strong> is on <code style={st.inlineCode}>student_info</code> — use the
              Student Information app instead. API: <code style={st.inlineCode}>/api/associations</code>.
            </p>
            <div style={st.field}>
              <label style={st.label}>Section title override</label>
              <input
                type="text"
                value={assocLabel}
                onChange={(e) => setAssocLabel(e.target.value)}
                placeholder="Default: Associations"
                style={st.input}
              />
            </div>
            <div style={st.field}>
              <label style={st.label}>Enabled association types (one per line: type: Label)</label>
              <textarea
                value={assocEnabledTypesText}
                onChange={(e) => setAssocEnabledTypesText(e.target.value)}
                rows={5}
                placeholder={'program_site: Program site\ntrack: Track (legacy — prefer student_info.track_period_id)'}
                style={st.textarea}
                spellCheck={false}
              />
              <p style={st.stGradeOptsHint}>
                Saved as <code style={st.inlineCode}>enabled_association_types</code>. For FA / vendor partner per
                student, enable <code style={st.inlineCode}>vendor_groups_periods_id</code> on the Student Information
                app assignment instead.
              </p>
            </div>
          </>
        )}

        {slug === APP_SLUGS.WORK_SAMPLES && (
          <>
            <p style={st.stDbNote}>
              Assign this app on the <strong>organization&apos;s group-period</strong> (not a student
              team). Role teams are
              limited to group-periods nested under that organization. TOR:{' '}
              <code style={st.inlineCode}>person_links</code> (
              <code style={st.inlineCode}>{TEACHER_OF_RECORD_LINK_TYPE}</code>). Vendor partner:{' '}
              <code style={st.inlineCode}>student_info.vendor_groups_periods_id</code> on each student profile.
            </p>
            {!linkedAssignmentGpId ? (
              <p style={st.stGradeOptsHint}>
                Select the organization group-period above first.
              </p>
            ) : wsOrgGpLoading ? (
              <p style={st.stGradeOptsHint}>Loading teams under organization…</p>
            ) : wsOrgGpError ? (
              <p style={st.metaError}>{wsOrgGpError}</p>
            ) : (
              <p style={st.stGradeOptsHint}>
                Teams under <strong>{wsOrgLabel || 'organization'}</strong> ({wsGpOptions.length} group-period
                {wsGpOptions.length === 1 ? '' : 's'}).
              </p>
            )}
            {WORK_SAMPLE_GP_ROLE_FIELDS.map((field) => (
              <div key={field.roleKey} style={st.field}>
                <label style={st.label}>
                  {field.label}
                  {field.required ? ' *' : ''}
                </label>
                <select
                  value={wsGpValues[field.roleKey] || ''}
                  onChange={(e) => setWsGpValue(field.roleKey, e.target.value)}
                  style={st.select}
                  disabled={!linkedAssignmentGpId || wsOrgGpLoading || Boolean(wsOrgGpError)}
                >
                  <option value="">Select group-period…</option>
                  {wsGpOptions.map((gp) => (
                    <option key={gp.id} value={gp.id}>
                      {formatGroupsPeriodLabel(gp)}
                    </option>
                  ))}
                </select>
                <p style={st.stGradeOptsHint}>{field.hint}</p>
              </div>
            ))}
            <div style={st.field}>
              <span style={st.label}>Teacher of Record (TOR)</span>
              <p style={st.stGradeOptsHint}>
                Fixed to <code style={st.inlineCode}>person_links</code> with type{' '}
                <code style={st.inlineCode}>{TEACHER_OF_RECORD_LINK_TYPE}</code>. Link TORs on each student profile;
                no group-period picker.
              </p>
            </div>
            <div style={st.field}>
              <span style={st.label}>Vendor partner</span>
              <p style={st.stGradeOptsHint}>
                Fixed to <code style={st.inlineCode}>student_info</code> column{' '}
                <code style={st.inlineCode}>vendor_groups_periods_id</code>. Assign the Student Information app on the
                Students group-period, enable the <strong>Vendor partner</strong> field, and set each
                student&apos;s FA team (e.g. FA01) on the profile. Vouchers snapshot{' '}
                <code style={st.inlineCode}>vendor_groups_periods_id</code> at creation.
              </p>
            </div>
            <div style={st.field}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span style={st.label}>Compliance flag reasons</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    style={st.stOrderBtn}
                    onClick={() => setWsFlagReasons([...DEFAULT_FLAG_REASON_CATALOG])}
                  >
                    Reset defaults
                  </button>
                  <button type="button" style={st.stOrderBtn} onClick={addWsFlagReason}>
                    + Add reason
                  </button>
                </div>
              </div>
              <p style={st.stGradeOptsHint}>
                Saved as <code style={st.inlineCode}>metadata.flag_reasons</code> on this org{' '}
                <code style={st.inlineCode}>app_assignment</code>. VDMs can select one or more when flagging portfolio
                evidence. Inactive reasons stay for audit on old flags but disappear from new flags.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                {wsFlagReasons.map((entry, index) => (
                  <div
                    key={`${entry.code || 'new'}-${index}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) minmax(120px, 0.7fr) auto auto',
                      gap: 8,
                      alignItems: 'center',
                      padding: 8,
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      background: entry.active === false ? '#f9fafb' : '#fff'
                    }}
                  >
                    <input
                      type="text"
                      value={entry.label || ''}
                      placeholder="Label shown to staff"
                      style={st.input}
                      onChange={(e) => {
                        const label = e.target.value;
                        const patch = { label };
                        if (!entry.code || entry.code === slugifyReasonCode(entry.label)) {
                          patch.code = slugifyReasonCode(label);
                        }
                        updateWsFlagReason(index, patch);
                      }}
                    />
                    <input
                      type="text"
                      value={entry.code || ''}
                      placeholder="code_slug"
                      style={st.input}
                      onChange={(e) => updateWsFlagReason(index, { code: slugifyReasonCode(e.target.value) })}
                    />
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.75rem' }}>
                      <input
                        type="checkbox"
                        checked={entry.active !== false}
                        onChange={(e) => updateWsFlagReason(index, { active: e.target.checked })}
                      />
                      Active
                    </label>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        type="button"
                        style={{ ...st.stOrderBtn, opacity: index === 0 ? 0.35 : 1 }}
                        disabled={index === 0}
                        onClick={() => moveWsFlagReason(index, -1)}
                        aria-label="Move up"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        style={{
                          ...st.stOrderBtn,
                          opacity: index === wsFlagReasons.length - 1 ? 0.35 : 1
                        }}
                        disabled={index === wsFlagReasons.length - 1}
                        onClick={() => moveWsFlagReason(index, 1)}
                        aria-label="Move down"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        style={st.stOrderBtn}
                        onClick={() => removeWsFlagReason(index)}
                        aria-label="Remove"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {slug === APP_SLUGS.PAYMENTS && (
          <>
            <p style={st.stDbNote}>
              <strong>payment_transactions</strong> — an event ledger per student{' '}
              <code style={st.inlineCode}>user_groups_periods_id</code> (payments, refunds, etc.); status is derived, not
              stored. The schedule (charges) is set per group-period on the{' '}
              <code style={st.inlineCode}>/payments</code> page and inherited by every student. API:{' '}
              <code style={st.inlineCode}>/api/payments</code>.
            </p>
            <div style={st.field}>
              <label style={st.label}>Section title override</label>
              <input
                type="text"
                value={payLabel}
                onChange={e => setPayLabel(e.target.value)}
                placeholder="Default: Payments"
                style={st.input}
              />
            </div>
          </>
        )}

        {slug === APP_SLUGS.ATTACHMENTS && (
          <>
            <div style={st.field}>
              <label style={st.label}>Section title override</label>
              <input
                type="text"
                value={attLabel}
                onChange={(e) => setAttLabel(e.target.value)}
                placeholder="Default: Attachments"
                style={st.input}
              />
            </div>
            <div style={{ ...st.field, display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                id={`att-allow-upload-${rel?.id || 'new'}`}
                type="checkbox"
                checked={attAllowUpload}
                onChange={(e) => setAttAllowUpload(e.target.checked)}
              />
              <label htmlFor={`att-allow-upload-${rel?.id || 'new'}`} style={{ ...st.label, marginBottom: 0, cursor: 'pointer' }}>
                Allow file uploads (off = URL/link only)
              </label>
            </div>
            <div style={st.field}>
              <label style={st.label}>Allowed categories (one per line or comma-separated)</label>
              <textarea
                value={attCategoriesText}
                onChange={(e) => setAttCategoriesText(e.target.value)}
                rows={3}
                placeholder="grad_plan&#10;enrollment_agreement"
                style={st.textarea}
                spellCheck={false}
              />
              <p style={st.stGradeOptsHint}>
                When set, the Category field appears in the profile UI. Leave empty to hide categories.
              </p>
            </div>
            <div style={st.field}>
              <label style={st.label}>Required attachments by title (one per line)</label>
              <textarea
                value={attRequiredText}
                onChange={(e) => setAttRequiredText(e.target.value)}
                rows={4}
                placeholder={'Graduation Plan\nSigned Enrollment Agreement'}
                style={st.textarea}
                spellCheck={false}
              />
              <p style={st.stGradeOptsHint}>
                Saved as <code style={st.inlineCode}>required_attachments</code>. Profile matches{' '}
                <code style={st.inlineCode}>documents.title</code> case-insensitively; missing items show in red.
              </p>
            </div>
          </>
        )}

        {showJsonFallback && (
          <div style={st.field}>
            <label style={st.label}>Metadata (JSON object)</label>
            <textarea
              value={jsonStr}
              onChange={e => setJsonStr(e.target.value)}
              rows={12}
              style={st.textarea}
              spellCheck={false}
            />
          </div>
        )}
    </>
  );
});

function AssignmentMetadataModal({
  rel,
  appSlug,
  appTitle,
  gpLabel,
  st,
  crudStyles,
  groupsPeriods,
  onClose,
  onSubmit
}) {
  const formRef = useRef(null);
  const [err, setErr] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSave = async () => {
    setErr('');
    let metadata;
    try {
      metadata = formRef.current?.buildMetadata?.() ?? {};
    } catch (e) {
      setErr(e.message || 'Invalid settings');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(metadata);
      onClose();
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={st.modalOverlay} onClick={() => !submitting && onClose()}>
      <div style={{ ...st.modal, ...st.metaModal }} onClick={e => e.stopPropagation()}>
        <h2 style={st.modalTitle}>Assignment settings</h2>
        <p style={st.metaSubtitle}>{appTitle} · {gpLabel}</p>
        {err ? <p style={st.metaError}>{err}</p> : null}

        <AssignmentSettingsForm
          ref={formRef}
          rel={rel}
          appSlug={appSlug}
          st={st}
          allowJsonFallback={true}
          groupsPeriods={groupsPeriods}
          assignmentParentGpId={rel?.parent_id}
        />

        <div style={st.modalFooter}>
          <button type="button" onClick={() => !submitting && onClose()} style={st.modalBtn} className={crudStyles.buttonNeutralClass}>
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={submitting} style={st.modalBtn} className={crudStyles.buttonPrimaryClass}>
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AssignGroupPeriodPicker({
  grouped,
  selectedId,
  onSelect,
  appSlug,
  hierarchyMeta,
  assignedGpIdsForApp,
  search,
  onSearchChange,
  loading,
  st
}) {
  const isWorkSamples = String(appSlug || '').toLowerCase() === APP_SLUGS.WORK_SAMPLES;

  return (
    <div style={st.gpPickerWrap}>
      {isWorkSamples ? (
        <p style={st.gpPickerHint}>
          Only the organization group-period (org root) can be selected for Work Samples. Nested
          teams are shown for context but cannot be assigned.
        </p>
      ) : null}
      <input
        type="search"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Search organizations, teams, or periods…"
        style={st.gpPickerSearch}
        disabled={loading}
      />
      {loading ? (
        <p style={st.gpPickerEmpty}>Loading organization tree…</p>
      ) : grouped.length === 0 ? (
        <p style={st.gpPickerEmpty}>No group-periods match your search.</p>
      ) : (
        <div style={st.gpPickerList} role="radiogroup" aria-label="Group-period">
          {grouped.map((group) => (
            <div key={group.organization} style={st.gpPickerOrgBlock}>
              <div style={st.gpPickerOrgTitle}>{group.organization}</div>
              {group.items.map((gp) => {
                const disabledReason = getAssignGpDisabledReason({
                  gp,
                  appSlug,
                  hierarchyMeta,
                  assignedGpIdsForApp
                });
                const disabled = Boolean(disabledReason);
                const selected = String(selectedId) === String(gp.id);
                const depth = Math.min(gp._depth || 0, 8);
                return (
                  <label
                    key={gp.id}
                    style={{
                      ...st.gpPickerRow,
                      ...(selected ? st.gpPickerRowSelected : {}),
                      ...(disabled ? st.gpPickerRowDisabled : {})
                    }}
                    title={disabledReason || undefined}
                  >
                    <input
                      type="radio"
                      name="assign-groups-period"
                      value={gp.id}
                      checked={selected}
                      disabled={disabled}
                      onChange={() => onSelect(String(gp.id))}
                    />
                    <span style={{ marginLeft: `${depth * 14}px`, flex: 1, minWidth: 0 }}>
                      {formatGroupsPeriodPickerLabel(gp)}
                      {isOrgRootGroupsPeriod(gp, hierarchyMeta) ? (
                        <span style={st.gpPickerOrgRootTag}> org root</span>
                      ) : null}
                    </span>
                  </label>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AddAppFromCatalogModal({
  pendingApps,
  selectedSlug,
  title,
  onSelectSlug,
  onClearSelection,
  onTitleChange,
  onConfirm,
  onClose,
  creating,
  st,
  crudStyles
}) {
  const selected = pendingApps.find((a) => a.slug === selectedSlug) || null;

  return (
    <div style={st.modalOverlay} onClick={() => !creating && onClose()}>
      <div style={{ ...st.modal, ...st.assignModalWide }} onClick={(e) => e.stopPropagation()}>
        <h2 style={st.modalTitle}>Add app to database</h2>
        <p style={st.metaSubtitle}>
          Apps are defined in code with a fixed slug. Pick one not yet in the database, optionally
          adjust the display title, then confirm.
        </p>

        {pendingApps.length === 0 ? (
          <p style={st.gpPickerEmpty}>All known apps are already in the database.</p>
        ) : !selected ? (
          <div style={st.catalogList}>
            {pendingApps.map((entry) => (
              <button
                key={entry.slug}
                type="button"
                style={st.catalogRow}
                onClick={() => onSelectSlug(entry.slug)}
              >
                <span style={st.catalogRowMain}>
                  <span style={st.catalogRowTitle}>{entry.defaultTitle}</span>
                  <code style={st.catalogRowSlug}>{entry.slug}</code>
                </span>
                <span style={st.catalogRowDesc}>{entry.description}</span>
              </button>
            ))}
          </div>
        ) : (
          <>
            <div style={st.catalogSelectedHead}>
              <button
                type="button"
                style={st.catalogBackBtn}
                onClick={onClearSelection}
                disabled={creating}
              >
                ← Back to list
              </button>
            </div>
            <div style={st.field}>
              <label style={st.label}>Display title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => onTitleChange(e.target.value)}
                style={st.input}
                disabled={creating}
              />
            </div>
            <div style={st.field}>
              <label style={st.label}>Slug (fixed)</label>
              <code style={st.catalogSlugReadonly}>{selected.slug}</code>
            </div>
            <p style={st.catalogConfirmDesc}>{selected.description}</p>
          </>
        )}

        <div style={st.modalFooter}>
          <button
            type="button"
            onClick={onClose}
            disabled={creating}
            style={st.modalBtn}
            className={crudStyles.buttonNeutralClass}
          >
            Cancel
          </button>
          {selected ? (
            <button
              type="button"
              onClick={onConfirm}
              disabled={creating || !title.trim()}
              style={st.modalBtn}
              className={crudStyles.buttonPrimaryClass}
            >
              {creating ? 'Adding…' : 'Add to database'}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const AppsManager = () => {
  const { fetchWithAuth } = useAuth();
  const [apps, setApps] = useState([]);
  const [groupsPeriods, setGroupsPeriods] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [showCreateAppModal, setShowCreateAppModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [deletingAppId, setDeletingAppId] = useState(null);
  const [deletingRelId, setDeletingRelId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [createCatalogSlug, setCreateCatalogSlug] = useState('');
  const [createAppTitle, setCreateAppTitle] = useState('');
  const [assignAppId, setAssignAppId] = useState('');
  const [assignGroupsPeriodsId, setAssignGroupsPeriodsId] = useState('');
  const [assignGpSearch, setAssignGpSearch] = useState('');
  const [assignSettingsError, setAssignSettingsError] = useState('');
  const [groupsPeriodsHierarchyMeta, setGroupsPeriodsHierarchyMeta] = useState({});
  const [gpHierarchyLoading, setGpHierarchyLoading] = useState(false);
  const assignSettingsRef = useRef(null);
  const [mainTab, setMainTab] = useState('apps');
  const [metaModal, setMetaModal] = useState(null);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadGroupsPeriodsHierarchy = useCallback(async (rows) => {
    if (!rows?.length) {
      setGroupsPeriodsHierarchyMeta({});
      return;
    }
    setGpHierarchyLoading(true);
    try {
      const meta = await buildGroupsPeriodsHierarchyMeta(rows, fetchWithAuth);
      setGroupsPeriodsHierarchyMeta(meta);
    } catch (error) {
      console.error('AppsManager: failed to build groups-periods hierarchy', error);
      setGroupsPeriodsHierarchyMeta({});
    } finally {
      setGpHierarchyLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    if (groupsPeriods.length) {
      loadGroupsPeriodsHierarchy(groupsPeriods);
    }
  }, [groupsPeriods, loadGroupsPeriodsHierarchy]);

  const loadAllData = async () => {
    setIsLoading(true);
    try {
      const [appsRes, gpRes, relRes] = await Promise.all([
        fetch('/api/apps'),
        fetch('/api/groups-periods'),
        fetch('/api/relationships?relationship_type=app_assignment')
      ]);
      const [appsData, gpData, relData] = await Promise.all([
        appsRes.json(),
        gpRes.json(),
        relRes.json()
      ]);
      if (appsData.success) setApps(appsData.data || []);
      if (gpData.success) setGroupsPeriods(gpData.data || []);
      if (relData.success) setRelationships(relData.data || []);
    } catch (error) {
      setModalData({ title: 'Load Failed', message: 'Failed to load data', details: error.message });
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const appAssignments = relationships.filter(
    r => r.relationship_type === 'app_assignment' &&
         r.parent_table === 'groups_periods' &&
         r.child_table === 'apps'
  );

  const assignmentsByAppId = useMemo(() => {
    const m = new Map();
    appAssignments.forEach(rel => {
      const key = String(rel.child_id);
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(rel);
    });
    return m;
  }, [appAssignments]);

  const assignmentsByGpId = useMemo(() => {
    const m = new Map();
    appAssignments.forEach(rel => {
      const key = String(rel.parent_id);
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(rel);
    });
    return m;
  }, [appAssignments]);

  const assignAppSlug = useMemo(
    () => (apps.find((a) => String(a.id) === String(assignAppId))?.slug || '').toLowerCase(),
    [apps, assignAppId]
  );

  const assignedGpIdsForAssignApp = useMemo(() => {
    const set = new Set();
    if (!assignAppId) return set;
    (assignmentsByAppId.get(String(assignAppId)) || []).forEach((rel) => {
      set.add(String(rel.parent_id));
    });
    return set;
  }, [assignAppId, assignmentsByAppId]);

  const groupedAssignGroupPeriods = useMemo(
    () =>
      groupGroupsPeriodsForPicker({
        groupsPeriods,
        hierarchyMeta: groupsPeriodsHierarchyMeta,
        search: assignGpSearch
      }),
    [groupsPeriods, groupsPeriodsHierarchyMeta, assignGpSearch]
  );

  useEffect(() => {
    if (!assignGroupsPeriodsId || !assignAppId) return;
    const gp = groupsPeriods.find((g) => String(g.id) === String(assignGroupsPeriodsId));
    const reason = getAssignGpDisabledReason({
      gp,
      appSlug: assignAppSlug,
      hierarchyMeta: groupsPeriodsHierarchyMeta,
      assignedGpIdsForApp: assignedGpIdsForAssignApp
    });
    if (reason) setAssignGroupsPeriodsId('');
  }, [
    assignAppId,
    assignAppSlug,
    assignGroupsPeriodsId,
    groupsPeriods,
    groupsPeriodsHierarchyMeta,
    assignedGpIdsForAssignApp
  ]);

  const sortedGroupsPeriods = useMemo(() => {
    return [...groupsPeriods].sort((a, b) => {
      const la = `${a.group_name || ''} ${a.period_title || ''}`;
      const lb = `${b.group_name || ''} ${b.period_title || ''}`;
      return la.localeCompare(lb);
    });
  }, [groupsPeriods]);

  const registeredAppSlugs = useMemo(
    () => new Set(apps.map((a) => String(a.slug || '').toLowerCase())),
    [apps]
  );

  const pendingAppCatalog = useMemo(
    () => listKnownAppDefinitions().filter((entry) => !registeredAppSlugs.has(entry.slug)),
    [registeredAppSlugs]
  );

  const resetCreateAppModal = () => {
    setCreateCatalogSlug('');
    setCreateAppTitle('');
  };

  const openCreateAppModal = () => {
    resetCreateAppModal();
    setShowCreateAppModal(true);
  };

  const closeCreateAppModal = () => {
    if (creating) return;
    setShowCreateAppModal(false);
    resetCreateAppModal();
  };

  const selectCatalogApp = (slug) => {
    const entry = listKnownAppDefinitions().find((e) => e.slug === slug);
    setCreateCatalogSlug(slug);
    setCreateAppTitle(entry?.defaultTitle || slug);
  };

  const handleCreateApp = async () => {
    const slug = createCatalogSlug;
    const title = createAppTitle?.trim();
    if (!slug) {
      setModalData({ title: 'Validation', message: 'Select an app from the catalog.', details: '' });
      setShowErrorModal(true);
      return;
    }
    if (!title) {
      setModalData({ title: 'Validation', message: 'Display title is required', details: '' });
      setShowErrorModal(true);
      return;
    }
    setCreating(true);
    try {
      const res = await fetch('/api/apps', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, slug })
      });
      const data = await res.json();
      if (data.success) {
        setModalData({ title: 'App added', message: `"${title}" (${slug}) is now in the database.`, details: '' });
        setShowSuccessModal(true);
        setShowCreateAppModal(false);
        resetCreateAppModal();
        await loadAllData();
      } else throw new Error(data.message || 'Create failed');
    } catch (error) {
      setModalData({ title: 'Add failed', message: 'Failed to add app', details: error.message });
      setShowErrorModal(true);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteApp = async (appId, appTitle) => {
    if (!window.confirm(`Delete app "${appTitle}"? This will not remove existing assignments (relationships).`)) return;
    setDeletingAppId(appId);
    try {
      const res = await fetch(`/api/apps/${appId}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setModalData({ title: 'App Deleted', message: `"${appTitle}" has been deleted.`, details: '' });
        setShowSuccessModal(true);
        await loadAllData();
      } else throw new Error(data.message || 'Delete failed');
    } catch (error) {
      setModalData({ title: 'Delete Failed', message: 'Failed to delete app', details: error.message });
      setShowErrorModal(true);
    } finally {
      setDeletingAppId(null);
    }
  };

  const handleAddAssignment = async () => {
    if (!assignAppId || !assignGroupsPeriodsId) {
      setModalData({ title: 'Validation', message: 'Select both an app and a group-period.', details: '' });
      setShowErrorModal(true);
      return;
    }
    const selectedGp = groupsPeriods.find((g) => String(g.id) === String(assignGroupsPeriodsId));
    const disabledReason = getAssignGpDisabledReason({
      gp: selectedGp,
      appSlug: assignAppSlug,
      hierarchyMeta: groupsPeriodsHierarchyMeta,
      assignedGpIdsForApp: assignedGpIdsForAssignApp
    });
    if (disabledReason) {
      setModalData({ title: 'Validation', message: disabledReason, details: '' });
      setShowErrorModal(true);
      return;
    }
    const exists = appAssignments.some(
      r => String(r.parent_id) === String(assignGroupsPeriodsId) && String(r.child_id) === String(assignAppId)
    );
    if (exists) {
      setModalData({ title: 'Already Assigned', message: 'This app is already assigned to the selected group-period.', details: '' });
      setShowErrorModal(true);
      return;
    }
    const selectedSlug = assignAppSlug;
    let metadata;
    if (slugHasInlineSettings(selectedSlug) && assignSettingsRef.current) {
      try {
        metadata = assignSettingsRef.current.buildMetadata();
        setAssignSettingsError('');
      } catch (e) {
        setAssignSettingsError(e.message || 'Invalid settings');
        return;
      }
    }
    setAssigning(true);
    try {
      const body = {
        parent_table: 'groups_periods',
        parent_id: assignGroupsPeriodsId,
        child_table: 'apps',
        child_id: assignAppId,
        relationship_type: 'app_assignment'
      };
      if (metadata && typeof metadata === 'object') body.metadata = metadata;
      const res = await fetch('/api/relationships', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.success) {
        setModalData({ title: 'Assignment Added', message: 'App has been assigned to the group-period.', details: '' });
        setShowSuccessModal(true);
        resetAssignModalState();
        await loadAllData();
      } else throw new Error(data.message || 'Add failed');
    } catch (error) {
      setModalData({ title: 'Add Failed', message: 'Failed to add assignment', details: error.message });
      setShowErrorModal(true);
    } finally {
      setAssigning(false);
    }
  };

  const handleSubmitAssignmentMeta = async (metadata) => {
    if (!metaModal?.rel?.id) return;
    const res = await fetch(`/api/relationships/${metaModal.rel.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ metadata })
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message || 'Failed to save assignment settings');
    setModalData({ title: 'Saved', message: 'Assignment settings updated for this group-period.', details: '' });
    setShowSuccessModal(true);
    await loadAllData();
  };

  const handleRemoveAssignment = async (relId) => {
    if (!window.confirm('Remove this app assignment?')) return;
    setDeletingRelId(relId);
    try {
      const res = await fetch(`/api/relationships/${relId}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setModalData({ title: 'Assignment Removed', message: 'App assignment has been removed.', details: '' });
        setShowSuccessModal(true);
        await loadAllData();
      } else throw new Error(data.message || 'Remove failed');
    } catch (error) {
      setModalData({ title: 'Remove Failed', message: 'Failed to remove assignment', details: error.message });
      setShowErrorModal(true);
    } finally {
      setDeletingRelId(null);
    }
  };

  const getAppTitle = (id) => apps.find(a => String(a.id) === String(id))?.title || `App ${id}`;
  const getAppSlug = (id) => apps.find(a => String(a.id) === String(id))?.slug || '';
  const getGpLabel = (gpId) => {
    const gp = groupsPeriods.find(g => String(g.id) === String(gpId));
    if (!gp) return `Group-Period ${gpId}`;
    return `${gp.group_name || '?'} — ${gp.period_title || '?'}`;
  };

  const resetAssignModalState = () => {
    setShowAssignModal(false);
    setAssignAppId('');
    setAssignGroupsPeriodsId('');
    setAssignGpSearch('');
    setAssignSettingsError('');
  };

  const closeAssignModal = () => {
    if (assigning) return;
    resetAssignModalState();
  };

  const openAssignModalForApp = (appId) => {
    setAssignAppId(String(appId));
    setAssignGroupsPeriodsId('');
    setAssignGpSearch('');
    setAssignSettingsError('');
    setShowAssignModal(true);
  };

  return (
    <div style={styles.container}>
      <LoadingOverlay isVisible={isLoading} message="Loading apps..." />

      <header style={styles.header}>
        <div style={styles.headerText}>
          <h1 style={styles.title}>Apps</h1>
          <p style={styles.description}>
            Assign apps to group-periods; they surface on User Profile when Page Permissions allow it.
            Per assignment, use the sliders icon to edit metadata (enabled fields, masks, labels)—the same keys User Profile reads from each relationship.
          </p>
        </div>
        <div style={styles.headerActions}>
          <button
            type="button"
            onClick={openCreateAppModal}
            style={styles.iconBtn}
            className={crudStyles.buttonPrimarySoftClass}
            title="Add app from catalog"
            aria-label="Add app from catalog"
          >
            <IconPlus />
          </button>
        </div>
      </header>

      <div style={styles.tabBar}>
        <button
          type="button"
          onClick={() => setMainTab('apps')}
          style={{ ...styles.tab, ...(mainTab === 'apps' ? styles.tabActive : {}) }}
        >
          By app
        </button>
        <button
          type="button"
          onClick={() => setMainTab('byGp')}
          style={{ ...styles.tab, ...(mainTab === 'byGp' ? styles.tabActive : {}) }}
        >
          By group-period
        </button>
      </div>

      <div style={styles.panel}>
        {mainTab === 'apps' && (
          <>
            {apps.length === 0 && !isLoading ? (
              <p style={styles.empty}>No apps in the database yet. Use + to add from the catalog.</p>
            ) : (
              <div style={styles.appGrid}>
                {apps.map(app => {
                  const key = String(app.id);
                  const forApp = assignmentsByAppId.get(key) || [];
                  const { description, appearsOn } = getAppSlugDetails(app.slug);

                  return (
                    <article key={app.id} style={styles.appCard}>
                      <div style={styles.appCardTop}>
                        <div style={styles.appCardTitleRow}>
                          <span style={styles.appTitle}>{app.title}</span>
                          <code style={styles.slug}>{app.slug}</code>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeleteApp(app.id, app.title)}
                          disabled={deletingAppId === app.id}
                          style={{
                            ...styles.iconBtnDanger,
                            opacity: deletingAppId === app.id ? 0.45 : 1
                          }}
                          title={deletingAppId === app.id ? 'Deleting…' : 'Delete app'}
                          aria-label="Delete app"
                        >
                          <IconTrash />
                        </button>
                      </div>

                      <p style={styles.appDescription}>{description}</p>
                      <div style={styles.appearsBlock}>
                        <span style={styles.appearsLabel}>Where it appears</span>
                        <p style={styles.appearsText}>{appearsOn}</p>
                      </div>

                      <div style={styles.assignBlock}>
                        <div style={styles.assignBlockHead}>
                          <span style={styles.appearsLabel}>Group-period assignments</span>
                          <button
                            type="button"
                            onClick={() => openAssignModalForApp(app.id)}
                            style={styles.iconBtnMuted}
                            className={crudStyles.buttonPrimarySoftClass}
                            title={`Assign ${app.title} to a group-period`}
                            aria-label={`Assign ${app.title} to a group-period`}
                          >
                            <IconLink />
                          </button>
                        </div>
                        <ul style={styles.assignList}>
                          {forApp.length === 0 ? (
                            <li style={styles.assignEmpty}>
                              Not linked yet — use the link button to assign.
                            </li>
                          ) : (
                          forApp.map(rel => (
                            <li key={rel.id} style={styles.assignRow}>
                              <span style={styles.assignLabel}>{getGpLabel(rel.parent_id)}</span>
                              <span style={styles.assignActions}>
                                <button
                                  type="button"
                                  onClick={() => setMetaModal({ rel, appChildId: app.id })}
                                  style={styles.iconBtnMuted}
                                  title="Assignment settings (metadata)"
                                  aria-label={`Configure assignment for ${getGpLabel(rel.parent_id)}`}
                                >
                                  <IconSliders />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveAssignment(rel.id)}
                                  disabled={deletingRelId === rel.id}
                                  style={{
                                    ...styles.iconBtnMuted,
                                    opacity: deletingRelId === rel.id ? 0.45 : 1
                                  }}
                                  title="Remove assignment"
                                  aria-label={`Remove assignment from ${getGpLabel(rel.parent_id)}`}
                                >
                                  <IconUnlink />
                                </button>
                              </span>
                            </li>
                          ))
                        )}
                        </ul>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </>
        )}

        {mainTab === 'byGp' && (
          <>
            {sortedGroupsPeriods.length === 0 && !isLoading ? (
              <p style={styles.empty}>No group-periods loaded.</p>
            ) : (
              <div style={styles.gpList}>
                {sortedGroupsPeriods.map(gp => {
                  const rels = assignmentsByGpId.get(String(gp.id)) || [];
                  return (
                    <div key={gp.id} style={styles.gpCard}>
                      <div style={styles.gpCardHead}>
                        <span style={styles.gpTitle}>{gp.group_name || '?'}</span>
                        <span style={styles.gpSep}>·</span>
                        <span style={styles.gpPeriod}>{gp.period_title || '?'}</span>
                      </div>
                      {rels.length === 0 ? (
                        <p style={styles.gpEmpty}>No apps assigned.</p>
                      ) : (
                        <ul style={styles.gpAppsList}>
                          {rels.map(rel => {
                            const slug = getAppSlug(rel.child_id);
                            const brief = getAppSlugDetails(slug).description;
                            return (
                            <li key={rel.id} style={styles.gpAppRow}>
                              <div style={styles.gpAppMain}>
                                <span style={styles.gpAppTitle}>{getAppTitle(rel.child_id)}</span>
                                <code style={styles.gpAppSlug}>{slug}</code>
                                <p style={styles.gpAppBrief}>{brief}</p>
                              </div>
                              <span style={styles.assignActions}>
                                <button
                                  type="button"
                                  onClick={() => setMetaModal({ rel, appChildId: rel.child_id })}
                                  style={styles.iconBtnMuted}
                                  title="Assignment settings (metadata)"
                                  aria-label={`Configure ${getAppTitle(rel.child_id)} for this group-period`}
                                >
                                  <IconSliders />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveAssignment(rel.id)}
                                  disabled={deletingRelId === rel.id}
                                  style={{
                                    ...styles.iconBtnMuted,
                                    opacity: deletingRelId === rel.id ? 0.45 : 1
                                  }}
                                  title="Remove assignment"
                                  aria-label={`Unassign ${getAppTitle(rel.child_id)}`}
                                >
                                  <IconUnlink />
                                </button>
                              </span>
                            </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {showCreateAppModal && (
        <AddAppFromCatalogModal
          pendingApps={pendingAppCatalog}
          selectedSlug={createCatalogSlug}
          title={createAppTitle}
          onSelectSlug={selectCatalogApp}
          onClearSelection={resetCreateAppModal}
          onTitleChange={setCreateAppTitle}
          onConfirm={handleCreateApp}
          onClose={closeCreateAppModal}
          creating={creating}
          st={styles}
          crudStyles={crudStyles}
        />
      )}

      {metaModal && (
        <AssignmentMetadataModal
          rel={metaModal.rel}
          appSlug={getAppSlug(metaModal.appChildId)}
          appTitle={getAppTitle(metaModal.appChildId)}
          gpLabel={getGpLabel(metaModal.rel.parent_id)}
          st={styles}
          crudStyles={crudStyles}
          groupsPeriods={groupsPeriods}
          onClose={() => setMetaModal(null)}
          onSubmit={handleSubmitAssignmentMeta}
        />
      )}

      {showAssignModal && (() => {
        const assignApp = apps.find((a) => String(a.id) === String(assignAppId));
        const showSettings = slugHasInlineSettings(assignAppSlug);
        return (
        <div style={styles.modalOverlay} onClick={() => !assigning && closeAssignModal()}>
          <div style={{ ...styles.modal, ...(showSettings ? styles.metaModal : {}), ...styles.assignModalWide }} onClick={e => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>
              {assignApp ? `Assign ${assignApp.title}` : 'Assign app'}
            </h2>
            {assignApp ? (
              <p style={styles.metaSubtitle}>
                <code style={styles.inlineCode}>{assignApp.slug}</code>
                {' — pick a group-period below.'}
              </p>
            ) : null}
            <div style={styles.field}>
              <label style={styles.label}>Group-Period</label>
              <AssignGroupPeriodPicker
                grouped={groupedAssignGroupPeriods}
                selectedId={assignGroupsPeriodsId}
                onSelect={setAssignGroupsPeriodsId}
                appSlug={assignAppSlug}
                hierarchyMeta={groupsPeriodsHierarchyMeta}
                assignedGpIdsForApp={assignedGpIdsForAssignApp}
                search={assignGpSearch}
                onSearchChange={setAssignGpSearch}
                loading={gpHierarchyLoading}
                st={styles}
              />
            </div>
            {showSettings && (
              <div style={styles.field}>
                <label style={styles.label}>Settings</label>
                {assignSettingsError ? <p style={styles.metaError}>{assignSettingsError}</p> : null}
                <AssignmentSettingsForm
                  key={`${assignAppSlug}-${assignGroupsPeriodsId}`}
                  ref={assignSettingsRef}
                  rel={null}
                  appSlug={assignAppSlug}
                  st={styles}
                  allowJsonFallback={false}
                  groupsPeriods={groupsPeriods}
                  assignmentParentGpId={assignGroupsPeriodsId}
                />
              </div>
            )}
            <div style={styles.modalFooter}>
              <button type="button" onClick={closeAssignModal} disabled={assigning} style={styles.modalBtn} className={crudStyles.buttonNeutralClass}>Cancel</button>
              <button type="button" onClick={handleAddAssignment} disabled={assigning || !assignAppId || !assignGroupsPeriodsId} style={styles.modalBtn} className={crudStyles.buttonPrimaryClass}>
                {assigning ? 'Adding...' : 'Add assignment'}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      <ErrorModal isOpen={showErrorModal} onClose={() => setShowErrorModal(false)} title={modalData?.title} message={modalData?.message} details={modalData?.details} />
      <SuccessModal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)} title={modalData?.title} message={modalData?.message} details={modalData?.details} />
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f4f4f5',
    padding: '14px 16px 28px',
    maxWidth: 1120,
    margin: '0 auto'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 12,
    paddingBottom: 12,
    borderBottom: '1px solid #e4e4e7'
  },
  headerText: { minWidth: 0 },
  title: { fontSize: 20, fontWeight: 600, color: '#18181b', margin: '0 0 4px 0', letterSpacing: '-0.02em' },
  description: { fontSize: 12.5, color: '#71717a', margin: 0, lineHeight: 1.45 },
  headerActions: { display: 'flex', gap: 6, flexShrink: 0 },
  iconBtn: {
    ...iconBtnBase,
    width: 34,
    height: 34,
    padding: 0,
    background: '#fff',
    color: '#3f3f46',
    border: '1px solid #e4e4e7',
    boxShadow: '0 1px 0 rgba(0,0,0,0.04)'
  },
  iconBtnDanger: {
    ...iconBtnBase,
    width: 30,
    height: 30,
    padding: 0,
    background: 'transparent',
    color: '#a1a1aa'
  },
  iconBtnMuted: {
    ...iconBtnBase,
    width: 28,
    height: 28,
    padding: 0,
    background: 'transparent',
    color: '#a1a1aa'
  },
  tabBar: {
    display: 'flex',
    gap: 4,
    marginBottom: 10
  },
  tab: {
    padding: '6px 12px',
    fontSize: 12.5,
    fontWeight: 500,
    color: '#71717a',
    background: 'transparent',
    border: 'none',
    borderRadius: 7,
    cursor: 'pointer'
  },
  tabActive: {
    color: '#18181b',
    background: '#fff',
    boxShadow: '0 0 0 1px #e4e4e7 inset'
  },
  panel: {
    background: 'transparent'
  },
  empty: { color: '#a1a1aa', fontSize: 13, margin: '12px 0' },
  appGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: 10
  },
  appCard: {
    background: '#fff',
    border: '1px solid #e4e4e7',
    borderRadius: 10,
    padding: '10px 11px 11px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
  },
  appCardTop: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 8
  },
  appCardTitleRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    minWidth: 0
  },
  appTitle: { fontSize: 13.5, fontWeight: 600, color: '#27272a', lineHeight: 1.25 },
  appDescription: {
    fontSize: 11.5,
    color: '#71717a',
    lineHeight: 1.45,
    margin: '0 0 8px 0'
  },
  appearsBlock: {
    marginBottom: 10,
    padding: '8px 9px',
    background: '#fafafa',
    borderRadius: 8,
    border: '1px solid #f4f4f5'
  },
  appearsLabel: {
    display: 'block',
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: '#a1a1aa',
    marginBottom: 4
  },
  appearsText: {
    fontSize: 11,
    color: '#52525b',
    lineHeight: 1.45,
    margin: 0
  },
  slug: {
    fontSize: 11,
    color: '#71717a',
    fontFamily: 'ui-monospace, monospace',
    background: '#fafafa',
    padding: '2px 6px',
    borderRadius: 4,
    alignSelf: 'flex-start',
    border: '1px solid #f4f4f5'
  },
  assignList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 3
  },
  assignBlock: {
    marginTop: 4,
    paddingTop: 10,
    borderTop: '1px solid #f4f4f5'
  },
  assignBlockHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 6
  },
  assignEmpty: {
    fontSize: 12,
    color: '#a1a1aa',
    padding: '8px 0',
    fontStyle: 'normal'
  },
  assignRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '6px 8px',
    background: '#fafafa',
    borderRadius: 7,
    border: '1px solid #f4f4f5'
  },
  assignLabel: { fontSize: 11.5, color: '#52525b', lineHeight: 1.35, flex: 1, minWidth: 0 },
  assignActions: { display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0 },
  gpList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },
  gpCard: {
    background: '#fff',
    border: '1px solid #e4e4e7',
    borderRadius: 10,
    padding: '10px 12px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
  },
  gpCardHead: {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 8,
    paddingBottom: 8,
    borderBottom: '1px solid #f4f4f5'
  },
  gpTitle: { fontSize: 13, fontWeight: 600, color: '#27272a' },
  gpSep: { color: '#d4d4d8', fontSize: 12 },
  gpPeriod: { fontSize: 12.5, color: '#71717a' },
  gpEmpty: { fontSize: 12, color: '#a1a1aa', margin: '4px 0 0' },
  gpAppsList: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 },
  gpAppRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
    padding: '8px 8px',
    background: '#fafafa',
    borderRadius: 7,
    border: '1px solid #f4f4f5'
  },
  gpAppMain: { flex: 1, minWidth: 0 },
  gpAppTitle: { fontSize: 12.5, fontWeight: 500, color: '#3f3f46', display: 'block' },
  gpAppBrief: {
    fontSize: 10.5,
    color: '#a1a1aa',
    lineHeight: 1.35,
    margin: '4px 0 0 0'
  },
  gpAppSlug: {
    fontSize: 10.5,
    color: '#a1a1aa',
    fontFamily: 'ui-monospace, monospace'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(24,24,27,0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(2px)'
  },
  modal: {
    background: '#fff',
    borderRadius: 12,
    padding: '20px 22px',
    maxWidth: 400,
    width: '90%',
    border: '1px solid #e4e4e7',
    boxShadow: '0 12px 40px rgba(0,0,0,0.12)'
  },
  metaModal: {
    maxWidth: 560,
    maxHeight: '90vh',
    overflowY: 'auto'
  },
  assignModalWide: {
    maxWidth: 520,
    width: '94%'
  },
  gpPickerWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },
  gpPickerHint: {
    margin: 0,
    fontSize: 11.5,
    lineHeight: 1.45,
    color: '#71717a',
    padding: '8px 10px',
    background: '#fafafa',
    borderRadius: 8,
    border: '1px solid #f4f4f5'
  },
  gpPickerSearch: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '8px 10px',
    border: '1px solid #e4e4e7',
    borderRadius: 8,
    fontSize: 13,
    background: '#fff'
  },
  gpPickerList: {
    maxHeight: 280,
    overflowY: 'auto',
    border: '1px solid #e4e4e7',
    borderRadius: 8,
    background: '#fafafa',
    padding: '6px 0'
  },
  gpPickerOrgBlock: {
    padding: '4px 0 8px'
  },
  gpPickerOrgTitle: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#71717a',
    padding: '6px 12px 4px'
  },
  gpPickerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '6px 12px',
    fontSize: 12.5,
    color: '#27272a',
    cursor: 'pointer',
    borderLeft: '3px solid transparent'
  },
  gpPickerRowSelected: {
    background: '#eef2ff',
    borderLeftColor: '#6366f1'
  },
  gpPickerRowDisabled: {
    opacity: 0.55,
    cursor: 'not-allowed',
    color: '#71717a'
  },
  gpPickerOrgRootTag: {
    fontSize: 10,
    fontWeight: 600,
    color: '#6366f1',
    textTransform: 'uppercase',
    letterSpacing: '0.03em'
  },
  gpPickerEmpty: {
    margin: 0,
    fontSize: 12,
    color: '#71717a',
    padding: '12px 10px'
  },
  catalogList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    maxHeight: 360,
    overflowY: 'auto',
    marginBottom: 4
  },
  catalogRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
    width: '100%',
    textAlign: 'left',
    padding: '10px 12px',
    border: '1px solid #e4e4e7',
    borderRadius: 8,
    background: '#fafafa',
    cursor: 'pointer'
  },
  catalogRowMain: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: 8,
    width: '100%'
  },
  catalogRowTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: '#27272a'
  },
  catalogRowSlug: {
    fontSize: 10.5,
    fontFamily: 'ui-monospace, monospace',
    color: '#71717a',
    background: '#fff',
    padding: '1px 6px',
    borderRadius: 4,
    border: '1px solid #f4f4f5'
  },
  catalogRowDesc: {
    fontSize: 11.5,
    lineHeight: 1.4,
    color: '#71717a',
    margin: 0
  },
  catalogSelectedHead: {
    marginBottom: 8
  },
  catalogBackBtn: {
    border: 'none',
    background: 'none',
    padding: 0,
    fontSize: 12,
    color: '#6366f1',
    cursor: 'pointer'
  },
  catalogSlugReadonly: {
    display: 'block',
    fontSize: 12,
    fontFamily: 'ui-monospace, monospace',
    color: '#52525b',
    background: '#fafafa',
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid #e4e4e7'
  },
  catalogConfirmDesc: {
    fontSize: 12,
    lineHeight: 1.45,
    color: '#71717a',
    margin: '0 0 8px 0'
  },
  metaSubtitle: {
    fontSize: 12.5,
    color: '#71717a',
    margin: '-10px 0 10px 0',
    lineHeight: 1.35
  },
  metaHint: {
    fontSize: 11,
    color: '#a1a1aa',
    margin: '0 0 14px 0',
    lineHeight: 1.45
  },
  metaError: {
    fontSize: 12,
    color: '#b91c1c',
    margin: '0 0 12px 0'
  },
  checkboxGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '6px 10px',
    marginTop: 6
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12,
    color: '#3f3f46',
    cursor: 'pointer'
  },
  textarea: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px 10px',
    border: '1px solid #e4e4e7',
    borderRadius: 8,
    fontSize: 12,
    fontFamily: 'ui-monospace, monospace',
    background: '#fafafa',
    resize: 'vertical'
  },
  stDbNote: {
    fontSize: 11,
    color: '#52525b',
    lineHeight: 1.5,
    margin: '0 0 14px 0',
    padding: '10px 11px',
    background: '#fafafa',
    borderRadius: 8,
    border: '1px solid #f4f4f5'
  },
  inlineCode: {
    fontFamily: 'ui-monospace, monospace',
    fontSize: 10.5,
    background: '#f4f4f5',
    padding: '1px 5px',
    borderRadius: 4
  },
  stCatalogHint: {
    fontSize: 10.5,
    color: '#a1a1aa',
    margin: '0 0 8px 0',
    lineHeight: 1.45
  },
  stFieldCatalog: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    maxHeight: 280,
    overflowY: 'auto',
    paddingRight: 4
  },
  stFieldRow: {
    border: '1px solid #f4f4f5',
    borderRadius: 8,
    padding: '8px 10px',
    background: '#fff'
  },
  stFieldRowTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    flexWrap: 'wrap'
  },
  stFieldOrderControls: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0
  },
  stPosBadge: {
    fontSize: 10,
    fontWeight: 700,
    color: '#71717a',
    minWidth: 20,
    textAlign: 'center',
    padding: '2px 6px',
    background: '#f4f4f5',
    borderRadius: 6
  },
  stFieldCheck: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    cursor: 'pointer',
    marginBottom: 0,
    flex: 1,
    minWidth: 0
  },
  stFieldTitle: { fontSize: 12.5, fontWeight: 600, color: '#27272a' },
  stFieldMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 6,
    marginLeft: 24,
    marginBottom: 2
  },
  stBadge: {
    fontSize: 9.5,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#6366f1',
    background: '#eef2ff',
    padding: '2px 6px',
    borderRadius: 4
  },
  stMono: {
    fontFamily: 'ui-monospace, monospace',
    fontSize: 11,
    color: '#3f3f46'
  },
  stDbType: { fontSize: 10, color: '#a1a1aa' },
  stFieldDetail: {
    fontSize: 10.5,
    color: '#71717a',
    margin: '4px 0 0 24px',
    lineHeight: 1.4
  },
  stMaskInline: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    margin: '8px 0 0 24px',
    fontSize: 11.5,
    color: '#52525b',
    cursor: 'pointer'
  },
  stGradeOptsInline: {
    margin: '8px 0 0 24px',
    paddingTop: 8,
    borderTop: '1px solid #f4f4f5'
  },
  stGradeOptsLabel: {
    display: 'block',
    fontSize: 11,
    fontWeight: 500,
    color: '#52525b',
    marginBottom: 5
  },
  stGradeOptsHint: {
    fontSize: 10,
    color: '#a1a1aa',
    margin: '6px 0 0 0',
    lineHeight: 1.35
  },
  stSubOption: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    margin: '8px 0 0 24px',
    padding: '8px 9px',
    background: '#fafafa',
    borderRadius: 8,
    border: '1px solid #f4f4f5',
    fontSize: 11,
    color: '#52525b',
    lineHeight: 1.4,
    cursor: 'default'
  },
  stOrderBtn: {
    width: 26,
    height: 26,
    padding: 0,
    fontSize: 12,
    border: '1px solid #e4e4e7',
    borderRadius: 6,
    background: '#fff',
    cursor: 'pointer',
    color: '#52525b'
  },
  modalTitle: { margin: '0 0 16px 0', fontSize: 17, fontWeight: 600, color: '#18181b' },
  field: { marginBottom: 12 },
  label: { display: 'block', marginBottom: 5, fontSize: 12, fontWeight: 500, color: '#52525b' },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '8px 10px',
    border: '1px solid #e4e4e7',
    borderRadius: 8,
    fontSize: 13,
    background: '#fafafa'
  },
  select: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '8px 10px',
    border: '1px solid #e4e4e7',
    borderRadius: 8,
    fontSize: 13,
    background: '#fafafa'
  },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 },
  modalBtn: { padding: '8px 14px', fontSize: 13 }
};

export default AppsManager;
